import type {ShellAccount} from '@wandb/forge-shell-contract';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

type UrlAccountBroadcastContextValue = {
  /** The account the mounted remote's URL names, or null if it names none. */
  broadcast: ShellAccount | null;
  /** Claims broadcast ownership and returns operations scoped to that owner. */
  claimUrlAccountBroadcast: () => UrlAccountBroadcastLease;
};

type UrlAccountBroadcastLease = {
  broadcast: (account: ShellAccount | null) => void;
  release: () => void;
};

const UrlAccountBroadcastContext =
  createContext<UrlAccountBroadcastContextValue | null>(null);

export const UrlAccountBroadcastProvider = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  const [broadcast, setReported] = useState<ShellAccount | null>(null);
  const ownerRef = useRef<symbol | null>(null);

  const claimUrlAccountBroadcast = useCallback(() => {
    // Each lease gets a unique owner token. A stale remote can neither update
    // nor clear the broadcast after a newer remote replaces this token.
    const owner = Symbol('url-account-broadcast-owner');
    ownerRef.current = owner;
    setReported(null);

    return {
      broadcast: (account: ShellAccount | null) => {
        if (ownerRef.current !== owner) {
          return;
        }
        setReported(account);
      },
      release: () => {
        if (ownerRef.current !== owner) {
          return;
        }
        ownerRef.current = null;
        setReported(null);
      },
    };
  }, []);

  const value = useMemo(
    () => ({broadcast, claimUrlAccountBroadcast}),
    [broadcast, claimUrlAccountBroadcast]
  );

  return (
    <UrlAccountBroadcastContext.Provider value={value}>
      {children}
    </UrlAccountBroadcastContext.Provider>
  );
};

export const useBroadcastUrlAccount = (): ShellAccount | null => {
  const context = useContext(UrlAccountBroadcastContext);
  return context?.broadcast ?? null;
};

export const useClaimUrlAccountBroadcast = () => {
  const context = useContext(UrlAccountBroadcastContext);
  return context?.claimUrlAccountBroadcast;
};

/**
 * Claims a lease on the URL account broadcast when a remote is mounted, and
 * releases it when the remote unmounts. Returns the broadcast function that
 * lets the remote tell the shell which account its URL names.
 */
export const useRemoteBroadcastUrlAccount = (
  enabled: boolean
): ((account: ShellAccount | null) => void) | undefined => {
  const claimUrlAccountBroadcast = useClaimUrlAccountBroadcast();
  const canClaim = enabled && claimUrlAccountBroadcast != null;
  const leaseRef = useRef<UrlAccountBroadcastLease | null>(null);

  useEffect(() => {
    if (!canClaim) {
      return;
    }

    const lease = claimUrlAccountBroadcast();
    leaseRef.current = lease;

    return () => {
      leaseRef.current = null;
      lease.release();
    };
  }, [canClaim, claimUrlAccountBroadcast]);

  const broadcast = useCallback((account: ShellAccount | null) => {
    leaseRef.current?.broadcast(account);
  }, []);

  return canClaim ? broadcast : undefined;
};
